'use client';

import { LogoFull } from '@ahamove/icons';
import { Disconnect, Empty } from '@ahamove/icons/empty';
import type { LoginResponse, ActivateResponse } from '@ahm/api-wrappers-core';
import { local } from '@ahm/common-helpers';
import { ErudaLoader } from '@ahm/react-ui/components/eruda-loader';
import { ResultBlock } from '@ahm/react-ui/components/result-block';
import { ThemeProvider } from '@ahm/react-ui/theme';
import FingerprintJS from '@fingerprintjs/fingerprintjs-pro';
import { GoogleOAuthProvider } from '@react-oauth/google';
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import type { AuthSession, RegisterRequestBody } from './api';
import AuthProtection from './components/auth-protection';
import { useAuthProviderValue } from './hooks/use-auth-provider-value';
import { useWebinappRestrictions } from './hooks/use-webinapp-restrictions';
import {
  LOCAL_STORAGE_KEY__FINGERPRINT,
  PUBLIC_PATHS,
  fingerprintApiKey,
  type AuthStatus,
} from './lib/constants';
import { usePathname, useSearchParams } from './lib/navigation';
import { QueryClientProvider } from './lib/react-query/query-client-provider';
import type { SocketClientConfig } from './lib/utils';
import type { Locales } from './locales';
import createTranslations from './locales/createTranslations';
import { systemLocales } from './locales/system';
import { AuthModalProvider } from './modals/auth-modal-provider';
import { AuthStoreProvider, useAuthStore } from './store/auth-store-provider';
import type { WebInAppConfig } from './webinapp';

export type SocialProvider = 'google' | 'facebook';

export interface AuthContextProps {
  status: AuthStatus | null;
  loading?: boolean;
  logout: (forceReload?: boolean) => void;
  handleActivateUser: (
    activeCode: string,
    data?: LoginResponse & { isSocket?: boolean }
  ) => void;
  handleUpdatePasscode: (passcode: string) => void;
  loginViaSocial: (response: any, provider: SocialProvider) => void;
  loginViaPhoneNumber: (mobile: string, otp_method?: string) => void;
  isWebInApp: boolean;
  webInAppConfig?: WebInAppConfig;
  loginData: LoginResponse;
  onReSendOTP: (phone: string) => void;
  handleRequestOTP: (res: LoginResponse) => void;
  onRegister: (values: RegisterRequestBody) => void;
  session: AuthSession | null;
  getSession: () => Promise<AuthSession | null>;
  registerData: { name: string; email: string };
  isLoadingUserProfile?: boolean;
  page?: string;
  handleLoginSuccess?: (data: LoginResponse | ActivateResponse) => void;
  handleForceLoginOTP: (mobile: string, resend?: boolean) => void;
  isForceLoginOTP: boolean;
  handleSkipCreatePasscode: () => void;
  isAuthMutationPending?: boolean;
  showModalSelectOtpChannel: boolean;
  setShowModalSelectOtpChannel: React.Dispatch<React.SetStateAction<boolean>>;
}

export const AuthContext = createContext<AuthContextProps | undefined>(
  undefined
);

export const useAuth = (): AuthContextProps => {
  const ctx = useContext(AuthContext);

  if (ctx === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};

export type AuthProviderProps = PropsWithChildren<{
  isMobile?: boolean;
  session: AuthSession | null;
  lang?: keyof Locales;
  webinappPaths?: string[];
  publicPaths?: string[];
  domain?: string;
  accountsEndpoint: string;
  googleClientId?: string;
  facebookAppId?: string;
  facebookAppVersion?: string;
  socketConfig?: SocketClientConfig;
  debug?: boolean;
}>;

const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  isMobile,
  lang = 'vi',
  domain = 'ahamove.com',
  webinappPaths = [],
  publicPaths = [],
  accountsEndpoint,
  googleClientId,
  facebookAppId,
  facebookAppVersion = 'v20.0',
  socketConfig,
  // session: initialSession,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const tSystem = createTranslations({ locales: systemLocales, lang });

  const searchParams = useSearchParams();
  const webInAppSearchParam = searchParams.get('webinapp');

  const {
    setDomain,
    setAccountsEndpoint,
    setGoogleClientId,
    setFacebookClientConfig,
  } = useAuthStore((state) => state);

  const [visitorId, setVisitorId] = useState<string | null>(
    local.getItem(LOCAL_STORAGE_KEY__FINGERPRINT)
  );

  const value = useAuthProviderValue({ isMobile, lang, socketConfig });

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    // normalizeAuthCookies();

    if (domain) {
      setDomain(domain);
    }

    if (accountsEndpoint) {
      setAccountsEndpoint(accountsEndpoint);
    }

    if (googleClientId) {
      setGoogleClientId(googleClientId);
    }

    if (facebookAppId) {
      setFacebookClientConfig({
        appId: facebookAppId,
        version: facebookAppVersion,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    domain,
    accountsEndpoint,
    googleClientId,
    facebookAppId,
    facebookAppVersion,
  ]);

  useLayoutEffect(() => {
    const DEFAULT_VISITOR_ID = 'web';

    if (!visitorId) {
      if (webInAppSearchParam) {
        setVisitorId(webInAppSearchParam ?? DEFAULT_VISITOR_ID);
        return;
      }

      if (!fingerprintApiKey) {
        setVisitorId(DEFAULT_VISITOR_ID);
        return;
      }

      // Initialize the agent on application start.
      // eslint-disable-next-line import/no-named-as-default-member
      const fpPromise = FingerprintJS.load({
        apiKey: fingerprintApiKey,
        region: 'ap',
      });

      // Get the visitorId when you need it.
      fpPromise
        .then((fp) => fp.get())
        .then((result) => {
          if (result.visitorId) {
            setVisitorId(result.visitorId);
            local.setItem(LOCAL_STORAGE_KEY__FINGERPRINT, result.visitorId);
          } else {
            console.error('Visitor ID not found');
            setVisitorId(DEFAULT_VISITOR_ID);
          }
        })
        .catch((error) => {
          console.error('Error getting fingerprint:', error);
          setVisitorId(DEFAULT_VISITOR_ID);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitorId, webInAppSearchParam]);

  const pathname = usePathname();
  const mergedPublicPaths = useMemo(
    () => [...PUBLIC_PATHS, ...publicPaths],
    [publicPaths]
  );

  const { showWebinappRestriction, showMobileRestriction, isProcessWebinapp } =
    useWebinappRestrictions({ pathname, isMobile: !!isMobile, webinappPaths });

  // Check if the current pathname is a public path
  const isPublicPath = mergedPublicPaths.includes(pathname);

  if (isProcessWebinapp) {
    return <ResultBlock icon={LogoFull} iconSize={44} />;
  }

  if (showWebinappRestriction) {
    return (
      <ResultBlock
        icon={Empty}
        title={tSystem('webinapp_only_title')}
        description={tSystem('webinapp_only_description')}
      />
    );
  }

  if (showMobileRestriction) {
    return (
      <ResultBlock
        icon={Disconnect}
        title={tSystem('access_denied_title')}
        description={tSystem('access_denied_description')}
      />
    );
  }

  const content = isPublicPath ? (
    <>{children}</>
  ) : (
    <AuthProtection>{children}</AuthProtection>
  );

  if (!googleClientId) {
    return <AuthContext.Provider value={value}>{content}</AuthContext.Provider>;
  }

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <AuthContext.Provider value={value}>{content}</AuthContext.Provider>
    </GoogleOAuthProvider>
  );
};

function AuthProviderWrapper({ children, debug, ...props }: AuthProviderProps) {
  return (
    <QueryClientProvider>
      <ThemeProvider>
        <AuthStoreProvider>
          <AuthModalProvider isMobile={props.isMobile} lang={props.lang}>
            <AuthProvider {...props}>
              {children}
              <ErudaLoader enabled={Boolean(props.isMobile && debug)} />
            </AuthProvider>
          </AuthModalProvider>
        </AuthStoreProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export { AuthProviderWrapper as AuthProvider };
